import { Dispatch, SetStateAction, useEffect } from 'react';
import './Expenses.scss';
import { NSDropdown, NSInput, NSLabel } from 'bricks';
import { ProFormaModelIds } from 'constants/proForma';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollar } from '@fortawesome/pro-regular-svg-icons';
import { faLeftRight } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { IExpense } from 'views/ProFormaWizard/BasicInformation/hooks/useExpenses';
import { OperatingExpenseCalculatedOn } from 'views/ProFormaWizard/constants';
import { BOEProFormaDiscrepancies } from 'views/ProFormaTable/types';
import RenderIf from 'components/RenderIf/RenderIf';
import BOEDiscrepancyIcon from 'views/ProFormaTable/components/BOEDiscrepancyIcon/BOEDiscrepancyIcon';
import { OPERATIONS_EXPANDABLE_SECTIONS } from 'views/ProFormaTable/constants';
import { COST_TYPES, IExpenses } from '../../types';
import ExpensesDetail from '../ExpensesDetail/ExpensesDetail';

export interface IExpensesProps {
    values: IExpenses;
    proFormaModelId: number;
    setExpensesValues: Dispatch<SetStateAction<IExpenses>>;
    setExpensesDetail: Dispatch<SetStateAction<IExpense[]>>;
    initialExpenses: IExpense[];
    showExpenseDetail: boolean;
    setShowExpenseDetail: (showExpenseDetail: boolean) => void;
    totalIncomeUnits: number;
    totalSFPerUnits: number;
    boeDiscrepancies?: BOEProFormaDiscrepancies;
}

const Expenses = ({
    values, proFormaModelId, setExpensesValues, setExpensesDetail, initialExpenses, showExpenseDetail, setShowExpenseDetail,
    totalIncomeUnits, totalSFPerUnits, boeDiscrepancies,
}: IExpensesProps) => {
    const filteredUnits = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)
        ? COST_TYPES
        : COST_TYPES.filter(unit => unit.value === 'sf');

    const handleInputChange = (key: string, value: number | string) => {
        setExpensesValues(prevValues => {
            const newValues = { ...prevValues };

            if (key === 'expenses') {
                if (prevValues.expensesType === 'sf') {
                    newValues.valuePerSfPerYear = Number(value);
                    delete newValues.valuePerUnitPerYear;
                } else {
                    newValues.valuePerUnitPerYear = Number(value);
                    delete newValues.valuePerSfPerYear;
                }
            }

            (newValues as any)[key] = value;
            return newValues;
        });
    };

    const handleDetailChange = (updatedExpenses: IExpense[]) => {
        setExpensesDetail(updatedExpenses);
    };

    const updateCosts = (
        type: 'expenses' | 'amountPerYear',
        newValue: number,
        relatedType: 'expenses' | 'amountPerYear',
        multiplierType: 'expensesType',
    ) => {
        const multiplier = values[multiplierType] === 'units' ? totalIncomeUnits : totalSFPerUnits;
        if (type === 'amountPerYear') {
            handleInputChange(type, newValue);
            handleInputChange(relatedType, newValue / (multiplier ?? 1));
        } else {
            handleInputChange(type, newValue);
            handleInputChange(relatedType, newValue * (multiplier ?? 1));
        }
    };

    const handleChangeExpensePerUnitType = (value: number) => {
        updateCosts('expenses', value, 'amountPerYear', 'expensesType');
        handleInputChange('calculatedOn', values.expensesType === 'units'
            ? OperatingExpenseCalculatedOn.VALUE_PER_UNIT_PER_YEAR
            : OperatingExpenseCalculatedOn.VALUE_PER_SF_PER_YEAR);
    };

    const handleChangeAmountPerYear = (value: number) => {
        updateCosts('amountPerYear', value, 'expenses', 'expensesType');
        handleInputChange('calculatedOn', OperatingExpenseCalculatedOn.AMOUNT_PER_YEAR);
    };

    const handleUpdateUnitType = (newUnitType: string) => {
        handleInputChange('expensesType', newUnitType);

        if (newUnitType === 'sf') {
            handleInputChange('valuePerSfPerYear', values.expenses);
            handleInputChange('valuePerUnitPerYear', 0);
        } else {
            handleInputChange('valuePerUnitPerYear', values.expenses);
            handleInputChange('valuePerSfPerYear', 0);
        }

        if (newUnitType === 'units') {
            handleInputChange('calculatedOn', OperatingExpenseCalculatedOn.VALUE_PER_UNIT_PER_YEAR);
        } else {
            handleInputChange('calculatedOn', OperatingExpenseCalculatedOn.VALUE_PER_SF_PER_YEAR);
        }
    };

    useEffect(() => {
        const multiplier = values.expensesType === 'units' ? totalIncomeUnits : totalSFPerUnits;
        handleInputChange('amountPerYear', values.expenses * (multiplier ?? 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.expensesType, totalIncomeUnits, totalSFPerUnits]);

    return (
        <div className="w-100">
            <div className="d-flex">
                <div>
                    <div className="Expenses__title">Expenses</div>
                    <div className="Expenses__subtitle">Overview of this investment’s total expenses</div>
                </div>
                <RenderIf isTrue={boeDiscrepancies ? !!boeDiscrepancies[OPERATIONS_EXPANDABLE_SECTIONS[2].number]?.length : false}>
                    <BOEDiscrepancyIcon
                        section={OPERATIONS_EXPANDABLE_SECTIONS[2].title}
                        metricNames={boeDiscrepancies ? boeDiscrepancies[OPERATIONS_EXPANDABLE_SECTIONS[2].number] : []}
                        className="ml-2 my-auto"
                    />
                </RenderIf>
            </div>
            <div
                className={classNames([
                    'd-flex flex-row align-items-center w-50 pb-3',
                    {
                        'Expenses__inputs-container--disabled': showExpenseDetail,
                    },
                ])}
            >
                <NSLabel className="text-dark font-weight-normal pt-1 mr-4" htmlFor="hardCosts">
                    Annual expenses
                </NSLabel>
                <NSInput
                    id="expenses"
                    dataTestId="expenses"
                    type="number"
                    name="expenses"
                    placeholder="Enter expenses"
                    disabled={showExpenseDetail}
                    prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                    value={values.expenses}
                    onChange={(e: { target: { value: number } }) => handleChangeExpensePerUnitType(Number(e.target.value))}
                    appendInputAddon={(
                        <NSDropdown
                            options={filteredUnits}
                            selectedOption={filteredUnits.find(option => option.value === values.expensesType)}
                            customSelectedOptionText={filteredUnits.find(option => option.value === values.expensesType)?.label}
                            handleSelectOption={option => handleUpdateUnitType(option.value)}
                            badgeColor=""
                            isBadgeToggle
                            toggleClassName="p-0 m-0"
                        />
                    )}
                />
                <FontAwesomeIcon icon={faLeftRight} className="mx-2" />
                <NSInput
                    id="totalHardCosts"
                    dataTestId="total-expenses"
                    type="number"
                    name="totalHardCosts"
                    disabled={showExpenseDetail}
                    value={values.amountPerYear}
                    onChange={(e: { target: { value: number } }) => handleChangeAmountPerYear(Number(e.target.value))}
                    prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                    appendInputAddon={<div>Total</div>}
                />
            </div>
            <ExpensesDetail
                initialValues={initialExpenses}
                proFormaModelId={proFormaModelId}
                showExpenseDetail={showExpenseDetail}
                setShowExpenseDetail={setShowExpenseDetail}
                onChange={handleDetailChange}
            />
        </div>
    );
};

export default Expenses;
